import { useCallback, useEffect, useMemo, useState } from "react";
import { Resolver, useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { TextInput, Button, Switch, CustomSelect } from "@/components";
import styles from "../style.module.scss";
import { useAlert } from "@/context/AlertContext";
import { userV2Schema } from "@/lib/validations/userV2";
import { apiV2 } from "@/lib/apiV2/apiV2";
import {
  UserV2Payload,
  UserV2,
  action,
  RoleV2Id,
  RoleV2Code,
  APPLICATION_ID,
} from "@/types";
import { useRolesV2 } from "@/hooks/useRolesV2";
import { useUserApplicationsV2 } from "@/hooks/useUserApplicationsV2";
import { Box, CircularProgress, IconButton, styled } from "@mui/material";
import DeleteSharpIcon from "@mui/icons-material/DeleteSharp";
import AddSharpIcon from "@mui/icons-material/AddSharp";
import { useRerenderTrigger } from "@/hooks/useRerenderTrigger";
import { TAG_TYPE_NAME, TagsHierarchyV2, TagTypeTagV2 } from "@/types/tagsV2";
import { sendProviderName } from "@/lib/utils/getProviderTags";
import { Mk2FastFormPart } from "./Mk2FastFormPart";
import { MarketCanvasFormPart } from "./MarketCanvasFormPart";
import { PlatformFormPart } from "./PlaformFormPart";
import { useRegionsV2 } from "@/hooks/userRegionsV2";
import { useTagsV2 } from "@/hooks/useTagsV2";
import { useQueries } from "@tanstack/react-query";
import { useUserMeV2Info } from "@/hooks/useUserMeV2Info";

export interface UserFormV2Props {
  onSubmit: (data: UserV2, operation: action) => void;
  selectedUser?: UserV2 | null;
  setModified: (checked: boolean) => void;
}

export interface UserV2FormValues {
  active: boolean;
  name: string;
  email: string;
  roles: {
    roleId: number;
    appId: number;
    tags: number[];
    cbsaTags?: number[];
    marketTags?: number[];
    regionTags?: number[];
    confidentialityLevel?: number;
    accessFeaturesTags?: number[];
  }[];
  tags: number[];
  providerName?: string;
}

const mapFormValuesToPayload = (
  values: UserV2FormValues,
  providerTagId: number | null,
  providerOptions: TagTypeTagV2[],
): UserV2Payload => {
  const applicationIdsSet = new Set<number>();

  values.roles.forEach((role) => {
    applicationIdsSet.add(role.appId);
  });

  const roleTags = values.roles.map((role) => {
    let allTags = [...role.tags];

    if (role.appId === APPLICATION_ID.MARKET_CANVAS) {
      if (role.cbsaTags && role.cbsaTags.length > 0) {
        allTags = [...allTags, ...role.cbsaTags];
      }
      if (role.confidentialityLevel && role.confidentialityLevel > 0) {
        allTags = [...allTags, role.confidentialityLevel];
      }
      if (role.accessFeaturesTags && role.accessFeaturesTags.length > 0) {
        allTags = [...allTags, ...role.accessFeaturesTags];
      }
    }

    if (
      role.appId === APPLICATION_ID.FAST_MK2 ||
      role.appId === APPLICATION_ID.PLATFORM
    ) {
      allTags = [
        ...allTags,
        ...(role.regionTags ?? []),
        ...(role.marketTags ?? []),
      ];
    }

    return {
      id: role.roleId,
      tags: Array.from(new Set<number>(allTags)),
    };
  });

  const providerInventoryRole = roleTags.find(
    (role) =>
      role.id === RoleV2Id.FAST_MK2_BASIC_PROVIDER ||
      role.id === RoleV2Id.FAST_MK2_ADVANCED_PROVIDER,
  );

  if (providerInventoryRole && providerTagId !== null) {
    const providerIds = providerOptions.map((provider) => provider.id);

    const tagsExceptProvider = providerInventoryRole.tags.filter(
      (tag) => !providerIds.includes(tag),
    );

    providerInventoryRole.tags = [...tagsExceptProvider, providerTagId!];
  }

  return {
    active: values.active,
    applications: Array.from(applicationIdsSet),
    clients: [],
    email: values.email,
    name: values.name,
    roles: roleTags,
    tags: [],
  };
};

const CircularProgressBox = styled(Box)({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  position: "absolute",
  zIndex: 10,
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
});

// TODO: remove specific tags logic when it will be possible to fetch tags by role id
export function UserFormV2({
  onSubmit,
  selectedUser,
  setModified,
}: UserFormV2Props) {
  const alert = useAlert();

  const { applicationOptions, loadingApplications } = useUserApplicationsV2();
  const { roles, loadingRoles } = useRolesV2();
  const { tags: providerTags } = useTagsV2(TAG_TYPE_NAME.PROVIDER);

  const [isFormLoading, setIsFormLoading] = useState<boolean>(false);

  const defaultValues = useMemo<UserV2FormValues>(
    () =>
      selectedUser
        ? {
            active: selectedUser.active,
            name: selectedUser.name,
            email: selectedUser.email,
            roles: selectedUser.role_associations.map((role) => {
              const cbsaTags = role.tags
                .filter((tag) => tag.tag_type === TAG_TYPE_NAME.CBSA)
                .map((tag) => tag.id);

              const confidentialityLevelTags = role.tags
                .filter(
                  (tag) => tag.tag_type === TAG_TYPE_NAME.CONFIDENTIALITY_LEVEL,
                )
                .map((tag) => tag.id);

              const accessFeaturesTags = role.tags
                .filter(
                  (tag) => tag.tag_type === TAG_TYPE_NAME.MC_FEATURES_ACCESS,
                )
                .map((tag) => tag.id);

              const marketTags = role.tags
                .filter((tag) => tag.tag_type === TAG_TYPE_NAME.MARKET)
                .map((tag) => tag.id);

              const regionTags = role.tags
                .filter((tag) => tag.tag_type === TAG_TYPE_NAME.REGION)
                .map((tag) => tag.id);

              const confidentialityLevel =
                confidentialityLevelTags.length > 0
                  ? confidentialityLevelTags[0]
                  : 0;

              const regularTags = role.tags
                .filter(
                  (tag) =>
                    tag.tag_type !== TAG_TYPE_NAME.CBSA &&
                    tag.tag_type !== TAG_TYPE_NAME.CONFIDENTIALITY_LEVEL &&
                    tag.tag_type !== TAG_TYPE_NAME.MC_FEATURES_ACCESS &&
                    tag.tag_type !== TAG_TYPE_NAME.MARKET,
                )
                .map((tag) => tag.id);

              return {
                roleId: role.id,
                appId: role.application_id,
                tags: regularTags,
                cbsaTags,
                confidentialityLevel,
                accessFeaturesTags,
                marketTags,
                regionTags,
              };
            }),
            tags: [],
          }
        : {
            active: true,
            name: "",
            email: "",
            roles: [
              {
                roleId: 0,
                appId: 0,
                tags: [],
                cbsaTags: [],
                marketTags: [],
                regionTags: [],
                confidentialityLevel: 0,
                accessFeaturesTags: [],
              },
            ],
            tags: [],
          },
    [selectedUser],
  );

  const {
    register,
    control,
    handleSubmit,
    getValues,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<UserV2FormValues>({
    resolver: yupResolver(userV2Schema) as Resolver<UserV2FormValues>,
    defaultValues,
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "roles",
  });

  const roleField = watch("roles");
  const isProviderInventoryManager = roleField.some(
    (role) =>
      role.roleId === RoleV2Id.FAST_MK2_BASIC_PROVIDER ||
      role.roleId === RoleV2Id.FAST_MK2_ADVANCED_PROVIDER,
  );

  const onSubmitInternal = useCallback(
    async (values: UserV2FormValues) => {
      try {
        setIsFormLoading(true);

        let providerTagId: number | null = null;

        if (isProviderInventoryManager && values.providerName) {
          const existingProvider = providerTags.find(
            (provider) =>
              provider.name.toLowerCase() ===
              values.providerName?.toLowerCase(),
          );

          if (existingProvider) {
            providerTagId = existingProvider.id;
          } else {
            const { data: providers } = await apiV2.tags.getTagByTagType(
              TAG_TYPE_NAME.PROVIDER,
            );

            const payload = {
              child_tags: [],
              parent_tags: [],
              name: values.providerName,
              tag_type_id: providers.id,
            };

            const providerResponse = await sendProviderName(payload);
            providerTagId = providerResponse.id;
          }
        }

        const userPayload = mapFormValuesToPayload(
          values,
          providerTagId,
          providerTags,
        );

        if (selectedUser) {
          const { data } = await apiV2.users.patch(
            selectedUser.id,
            userPayload,
          );
          onSubmit(data, "edit");
        } else {
          const { data } = await apiV2.users.post(userPayload);
          onSubmit(data, "create");
        }
        setIsFormLoading(false);
      } catch (e: any) {
        setIsFormLoading(false);
        console.error("Error", JSON.stringify(e));
        alert("error", e, e?.response?.data?.status);
      }
    },
    [alert, onSubmit, selectedUser, isProviderInventoryManager, providerTags],
  );

  const activeInputProps = useMemo(() => register("active"), [register]);

  const handleActiveChange = useCallback(
    (checked: boolean) =>
      activeInputProps.onChange({
        target: { name: activeInputProps.name, value: checked },
      }),
    [activeInputProps],
  );

  useEffect(() => {
    setModified(isDirty);
  }, [setModified, isDirty]);

  const triggerRerender = useRerenderTrigger();

  const { regionOptions, loadingRegions } = useRegionsV2();

  const allRoleRegionTags = roleField.map(
    (_, index) => watch(`roles.${index}.regionTags`) || [],
  );

  const allRegionsFromRoles = useMemo(() => {
    const allRegions = allRoleRegionTags
      .flat()
      .filter((region): region is number => typeof region === "number");
    return Array.from(new Set(allRegions));
  }, [allRoleRegionTags]);

  const regionsToFetchMarkets = allRegionsFromRoles.filter((region) =>
    regionOptions.map((option) => option.value).includes(region),
  );

  const marketsPerRegion = useQueries({
    queries: regionsToFetchMarkets.map((region) => ({
      queryKey: ["marketsPerRegion", region],
      queryFn: () => apiV2.tags.getByTagId(region),
      enabled: !!region,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    })),
  });

  const allMarketsData: TagsHierarchyV2[] = marketsPerRegion
    .map((data) => data.data?.data)
    .flat()
    .filter((item): item is TagsHierarchyV2 => item !== undefined);

  return (
    <form className={styles.form} onSubmit={handleSubmit(onSubmitInternal)}>
      <header>
        <Button type="submit">
          {isFormLoading ? <CircularProgress size="30px" /> : "Save"}
        </Button>
        <h2 className={styles.title}>
          {selectedUser ? "Edit User" : "New User"}
        </h2>
      </header>
      <div className={`${styles.section} ${styles.content}`}>
        <TextInput
          label="User Name*"
          {...register("name", { required: true })}
          error={!!errors.name}
          helperText={errors.name?.message}
        />
        <TextInput
          label="Email"
          required
          {...register("email", { required: true })}
          disabled={!!selectedUser}
          error={!!errors.email}
          helperText={errors.email?.message}
        />
      </div>

      <div className={styles.section}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mr={-1.5}
        >
          <h6 className={`${styles.subtitle} ${styles.mb10}`}>Roles</h6>
          <IconButton
            onClick={() =>
              append({
                roleId: 0,
                appId: 0,
                tags: [],
                cbsaTags: [],
                confidentialityLevel: 0,
              })
            }
            className={styles.addRoleButton}
          >
            <AddSharpIcon />
          </IconButton>
        </Box>
        {fields.map((field, index) => {
          const appFieldProps = register(`roles.${index}.appId`, {
            required: true,
          });

          const currentRoleId = getValues(`roles.${index}.roleId`);

          const handleAppChange = (value: string | number) => {
            const newAppId = Number(value);
            const currentAppId = getValues(`roles.${index}.appId`);

            if (currentAppId !== newAppId) {
              const newAppRoles = roles.filter(
                (role) => role.application_id === newAppId,
              );

              // Check if defaultValues has this index
              const defaultRole = defaultValues.roles[index];
              if (defaultRole && newAppId === defaultRole.appId) {
                setValue(`roles.${index}.roleId`, defaultRole.roleId);
                setValue(`roles.${index}.tags`, defaultRole.tags || []);
              } else {
                setValue(`roles.${index}.tags`, []);
              }

              if (newAppId === APPLICATION_ID.MARKET_CANVAS) {
                setValue(
                  `roles.${index}.cbsaTags`,
                  defaultRole?.cbsaTags || [],
                );
                setValue(
                  `roles.${index}.confidentialityLevel`,
                  defaultRole?.confidentialityLevel || 0,
                );
              }

              if (newAppId === APPLICATION_ID.FAST_MK2) {
                setValue(
                  `roles.${index}.marketTags`,
                  defaultRole?.marketTags || [],
                );
              }
            }

            appFieldProps.onChange({
              target: { name: appFieldProps.name, value: newAppId },
            });
            triggerRerender();
          };

          const roleFieldProps = register(`roles.${index}.roleId`, {
            required: true,
          });

          const handleRolesChange = (value: string | number) => {
            roleFieldProps.onChange({
              target: { name: roleFieldProps.name, value: Number(value) },
            });
            triggerRerender();
          };

          const selectedAppId = getValues(`roles.${index}.appId`);

          const roleOptions = roles
            .filter(
              (role) =>
                role.application_id === +getValues(`roles.${index}.appId`),
            )
            .map((role) => ({
              label: role.name,
              value: role.id,
            }));

          const regionTags = watch(`roles.${index}.regionTags`);

          const handleRegionsChange = (selectedRegions: number[]) => {
            setValue(`roles.${index}.regionTags`, selectedRegions, {
              shouldValidate: true,
              shouldDirty: true,
            });

            const currentTags = watch(`roles.${index}.tags`) || [];
            const currentMarketTags = watch(`roles.${index}.marketTags`) || [];

            const allRegionIds = regionOptions.map((r) => r.value);
            const allMarketIds = allMarketsData.flatMap(
              (region) =>
                region?.child_tags?.flatMap(
                  (country) =>
                    country?.child_tags?.flatMap(
                      (state) =>
                        state?.child_tags?.map((market) => market.id) || [],
                    ) || [],
                ) || [],
            );

            const cleanBaseTags = currentTags.filter(
              (tag) =>
                !allRegionIds.includes(tag) && !allMarketIds.includes(tag),
            );

            setValue(`roles.${index}.tags`, cleanBaseTags);

            if (allMarketsData.length > 0) {
              const availableMarketIds =
                selectedRegions.length > 0
                  ? allMarketsData
                      .filter((region) => selectedRegions.includes(region.id))
                      .flatMap(
                        (region) =>
                          region?.child_tags?.flatMap(
                            (country) =>
                              country?.child_tags?.flatMap(
                                (state) =>
                                  state?.child_tags?.map(
                                    (market) => market.id,
                                  ) || [],
                              ) || [],
                          ) || [],
                      )
                      .filter(Boolean)
                  : [];

              const validMarketTags = currentMarketTags.filter((marketId) =>
                availableMarketIds.includes(marketId),
              );

              if (validMarketTags.length !== currentMarketTags.length) {
                setValue(`roles.${index}.marketTags`, validMarketTags);
              }
            }
          };

          const currentCbsaTags = watch(`roles.${index}.cbsaTags`);
          const currentConfidentialityLevel = watch(
            `roles.${index}.confidentialityLevel`,
          );
          const currentAccessableFeatures = watch(
            `roles.${index}.accessFeaturesTags`,
          );
          const currentAgileMarketTags = watch(`roles.${index}.marketTags`);

          const marketsForThisRole = allMarketsData.filter((region) =>
            (regionTags || []).includes(region.id),
          );

          const isMarketsLoading = marketsPerRegion.some(
            (market) => market.isLoading,
          );

          const handleCbsaTagsChange = (selectedCbsaTags: number[]) => {
            setValue(`roles.${index}.cbsaTags`, selectedCbsaTags);
          };

          const handleAccessebleFeaturesChange = (
            selectedFeatures: number[],
          ) => {
            setValue(`roles.${index}.accessFeaturesTags`, selectedFeatures);
          };

          const handleConfidentialityLevelChange = (
            confidentialityLevel: number,
          ) => {
            setValue(
              `roles.${index}.confidentialityLevel`,
              confidentialityLevel,
            );
          };

          const handleAgileMarketsChange = (selectedMarketTags: number[]) => {
            setValue(`roles.${index}.marketTags`, selectedMarketTags);
          };

          const getDefaultProviderValue = () => {
            if (!(selectedUser && isProviderInventoryManager)) return "";

            const providerInventoryRole = selectedUser.role_associations.find(
              (role) =>
                role.code === RoleV2Code.FAST_MK2_BASIC_PROVIDER ||
                role.code === RoleV2Code.FAST_MK2_ADVANCED_PROVIDER,
            );

            const providerName = providerInventoryRole?.tags.filter(
              (tag) => tag.tag_type === TAG_TYPE_NAME.PROVIDER,
            )[0]?.name;

            return providerName;
          };

          return (
            <Box key={field.id} display="flex" flexDirection="column">
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mr={-1.5}
              >
                <span className={styles.roleTitle}>Role {index + 1}</span>
                <IconButton
                  onClick={() => remove(index)}
                  className={styles.removeButton}
                >
                  <DeleteSharpIcon />
                </IconButton>
              </Box>
              <Box display="flex" flexDirection="column" gap={2} py={2}>
                <CustomSelect
                  label="Select Application*"
                  options={applicationOptions}
                  error={!!errors.roles?.[index]?.appId}
                  helperText={errors.roles?.[index]?.appId?.message}
                  {...appFieldProps}
                  onChange={handleAppChange}
                  value={getValues(`roles.${index}.appId`)}
                  placeholder="Select Application"
                  loading={loadingApplications}
                />
                <CustomSelect
                  key={getValues(`roles.${index}.appId`).toString()}
                  label="Select Role*"
                  options={roleOptions}
                  error={!!errors.roles?.[index]?.roleId}
                  helperText={errors.roles?.[index]?.roleId?.message}
                  disabled={!watch(`roles.${index}.appId`)}
                  {...roleFieldProps}
                  onChange={handleRolesChange}
                  value={getValues(`roles.${index}.roleId`)}
                  placeholder="Select User Role"
                  loading={loadingRoles}
                />
                {selectedAppId === APPLICATION_ID.FAST_MK2 && (
                  // TODO: add form array index!
                  <Mk2FastFormPart
                    control={control}
                    index={index}
                    currentTags={regionTags || []}
                    errors={errors}
                    getDefaultProviderValue={getDefaultProviderValue}
                    handleRegionsChange={handleRegionsChange}
                    isProviderInventoryManager={isProviderInventoryManager}
                    providerOptions={providerTags}
                    regionOptions={regionOptions}
                    markets={marketsForThisRole}
                    handleAgileMarketsChange={handleAgileMarketsChange}
                    currentAgileMarketTags={currentAgileMarketTags || []}
                    isMarketsLoading={isMarketsLoading}
                    isRegionsLoading={loadingRegions}
                    disabled={
                      !regionTags || regionTags.length === 0 || loadingRegions
                    }
                  />
                )}
                {selectedAppId === APPLICATION_ID.MARKET_CANVAS && (
                  <MarketCanvasFormPart
                    control={control}
                    currentCbsaTags={currentCbsaTags || []}
                    currentAccessableFeatures={currentAccessableFeatures || []}
                    currentConfidentialityLevel={
                      currentConfidentialityLevel || 0
                    }
                    errors={errors}
                    handleCbsaTagsChange={handleCbsaTagsChange}
                    handleConfidentialityLevelChange={
                      handleConfidentialityLevelChange
                    }
                    handleAccessebleFeaturesChange={
                      handleAccessebleFeaturesChange
                    }
                    index={index}
                  />
                )}
                {selectedAppId === APPLICATION_ID.PLATFORM &&
                  currentRoleId !== RoleV2Id.PLATFORM_SUPER_ADMIN && (
                    // TODO: add form array index!
                    <PlatformFormPart
                      control={control}
                      index={index}
                      errors={errors}
                      regionOptions={regionOptions}
                      handleRegionsChange={handleRegionsChange}
                      currentTags={regionTags || []}
                      markets={marketsForThisRole}
                      handleAgileMarketsChange={handleAgileMarketsChange}
                      currentAgileMarketTags={currentAgileMarketTags || []}
                      loading={isMarketsLoading}
                      disabled={!regionTags || regionTags.length === 0}
                    />
                  )}
              </Box>
            </Box>
          );
        })}
      </div>
      <div className={styles.section}>
        <label className={styles.switch} htmlFor="active">
          <h6 className={styles.subtitle}>Activate User</h6>
          <Switch
            id="active"
            defaultChecked={defaultValues.active}
            onCheckedChange={handleActiveChange}
          />
        </label>
      </div>
    </form>
  );
}
